/**
 * The engine half of the Materials panel (ADR-020 T2): discover a subject's
 * standard PBR materials and apply the Document's factor edits to the LIVE
 * scene. Lives in viewport/ because it holds three types — the panel and
 * main stay engine-free and consume this seam.
 *
 * Shipped values are snapshotted per material the first time a scene is
 * touched (a WeakMap, so a replaced subject releases its entry): the apply
 * step must be able to RESTORE a material whose edit was undone, and the
 * live object is mutated in place so "what did the file say?" has to be
 * remembered before the first write.
 *
 * Channel notes, engine⇄glTF:
 *  - metalness/roughness ⇄ metallicFactor/roughnessFactor, direct.
 *  - base color and emissive live in three's WORKING color space (linear
 *    sRGB) — the same space as glTF factors, so snapshots and edits pass
 *    through with no conversion; GLTFExporter writes them as
 *    baseColorFactor/emissiveFactor.
 *  - normal scale is three's Vector2; glTF carries ONE scalar on
 *    normalTexture. The edit sets the MAGNITUDE of both components,
 *    preserving each component's sign — GLTFLoader encodes the file's
 *    y-flip convention as a negative component, and stomping it would
 *    invert every bump.
 *  - occlusion strength ⇄ aoMapIntensity.
 *
 * Identity is the glTF material NAME (what the export carries; the Document
 * speaks it too). A file with duplicate material names applies one edit to
 * all of them — but each material falls back to ITS OWN shipped snapshot,
 * never a namesake's (Codex P2, #87): the panel row shows the first
 * bearer's values, the apply step restores every bearer's file truth. An
 * anonymous material keeps ONE synthetic identity however many meshes
 * share it (Codex P1, #87) — a shared object registered under two names
 * would let the later row's apply stomp the earlier row's edit.
 * Non-standard materials (MToon and friends) are invisible here: no factor
 * slots to move, so they are neither listed nor touched.
 *
 * Depends on: three. Depended on by: main (registration + display filter).
 */
import { Mesh, MeshStandardMaterial, Object3D, type Texture } from 'three';
import { roughnessCeilingFor } from '@harryhausen/kernel';

/** Linear-space RGB triple — glTF factor shape, three working-space values. */
export type Rgb = readonly [number, number, number];

export interface MaterialFactorTarget {
  readonly name: string;
  readonly shippedMetallic: number;
  readonly shippedRoughness: number;
  readonly shippedNormalScale: number;
  readonly shippedOcclusionStrength: number;
  readonly shippedBaseColor: Rgb;
  readonly shippedEmissive: Rgb;
  /** True = a roughness texture exists to bake >1 factors into at export. */
  readonly hasRoughnessTexture: boolean;
  /** glTF packs both in one texture, but three reads them through two slots. */
  readonly hasMetallicTexture: boolean;
  /** How many material objects wear this name — namesakes share one row. */
  readonly bearerCount: number;
  /**
   * TRUE when namesakes ship DIFFERENT scalars on THAT channel. Per channel,
   * not combined: bearers agreeing on roughness but differing on metallic
   * would otherwise block a perfectly unambiguous roughness detach
   * (Codex P2, #115).
   */
  readonly bearersDisagreeMetallic: boolean;
  readonly bearersDisagreeRoughness: boolean;
  /**
   * The largest roughness factor this material's slider should offer — the one
   * that reaches fully matte, MEASURED from its own roughness texture.
   *
   * A fixed ceiling cannot serve two generations of source: the satin class
   * ships roughness ~0.5 where x2 is exactly right, and the newer glossy class
   * ships ~0.04, where x2 leaves a mirror. See `roughnessCeilingFor`.
   */
  readonly roughnessCeiling: number;
  /** The measured median of the roughness channel, or null when unmeasurable. */
  readonly measuredRoughness: number | null;
  /** normalScale is a normal-TEXTURE attribute — without the map it is moot. */
  readonly hasNormalTexture: boolean;
  /** occlusionStrength is an occlusion-TEXTURE attribute — same law. */
  readonly hasOcclusionTexture: boolean;
}

/**
 * The ceiling to offer when a roughness texture exists but cannot be sampled
 * (no DOM, a tainted canvas, an image the 2D context will not draw). The
 * previous fixed ceiling, so an unmeasurable material behaves exactly as it
 * did before this became adaptive — never worse.
 */
const LEGACY_ROUGHNESS_CEILING = 2;

/** How many samples a side the roughness map is reduced to before reading. */
const ROUGHNESS_SAMPLE = 32;

/**
 * The median of a roughness map's GREEN channel — glTF packs roughness there.
 *
 * Sampled by drawing the map into a small canvas, so a 4096² texture costs one
 * scaled draw and 1024 reads. Returns null wherever that cannot be done (node,
 * a tainted canvas, an unsupported image source) — the caller then keeps the
 * legacy ceiling rather than guessing, because a wrong ceiling is worse than
 * an unchanged one.
 */
export function medianRoughness(map: Texture | null): number | null {
  if (map === null || typeof document === 'undefined') return null;
  const image = map.image as CanvasImageSource | null | undefined;
  if (image === null || image === undefined) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = ROUGHNESS_SAMPLE;
    canvas.height = ROUGHNESS_SAMPLE;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) return null;
    context.drawImage(image, 0, 0, ROUGHNESS_SAMPLE, ROUGHNESS_SAMPLE);
    const { data } = context.getImageData(0, 0, ROUGHNESS_SAMPLE, ROUGHNESS_SAMPLE);
    const green: number[] = [];
    for (let i = 1; i < data.length; i += 4) green.push(data[i]!);
    if (green.length === 0) return null;
    green.sort((a, b) => a - b);
    return green[Math.floor(green.length / 2)]! / 255;
  } catch {
    return null;
  }
}

/** One material's own file truth — the restore target when its edit is undone. */
interface ShippedFactors {
  /** The maps the FILE attached, so detaching is reversible per material. */
  readonly metalnessMap: Texture | null;
  readonly roughnessMap: Texture | null;
  readonly metallic: number;
  readonly roughness: number;
  readonly normalScale: number;
  readonly occlusionStrength: number;
  readonly baseColor: Rgb;
  readonly emissive: Rgb;
}

interface Bearer {
  readonly material: MeshStandardMaterial;
  readonly shipped: ShippedFactors;
}

interface Entry {
  /** Mutable: namesakes aggregate into one row, and the ceiling follows the glossiest. */
  target: MaterialFactorTarget;
  readonly bearers: Bearer[];
}

const scanned = new WeakMap<Object3D, Map<string, Entry>>();

function scan(scene: Object3D): Map<string, Entry> {
  const hit = scanned.get(scene);
  if (hit !== undefined) return hit;
  const entries = new Map<string, Entry>();
  // A shared anonymous material must resolve to the SAME synthetic name on
  // every encounter — one identity, one row, one apply.
  // Identity is a REAL NAME now, minted into the asset by the loader when the
  // file left one unnamed (load-glb) — so there is no synthetic namespace to
  // keep disjoint, no anonymity to track, and no unaddressable export to
  // refuse. That apparatus produced a P1 in three consecutive review rounds
  // while defending a case the corpus has never contained: 37 specimens, 37
  // named materials, zero unnamed (measured 2026-08-27; Tripo mints
  // `tripo_mat_<uuid>` itself). The fallback below mints in place for any
  // other loader, so the invariant holds everywhere: every material has a name
  // that travels with it.
  // The fallback minter must RESERVE the names already in the scene: an FBX (or
  // any non-GLB path) carrying an authored `material_x1` beside an unnamed
  // material would otherwise be handed the same name, `scan` would merge the
  // two objects into one row, and a name-wide factor or map-use edit would
  // silently move both (Codex P2, #115 — the same collision the deleted
  // synthetic namespace used to make, now in its replacement).
  const takenNames = new Set<string>();
  scene.traverse((node) => {
    if (!(node instanceof Mesh)) return;
    for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
      if (material instanceof MeshStandardMaterial && material.name !== '') takenNames.add(material.name);
    }
  });
  let lateMint = 0;
  scene.traverse((node) => {
    if (!(node instanceof Mesh)) return;
    const materials = Array.isArray(node.material) ? node.material : [node.material];
    for (const material of materials) {
      if (!(material instanceof MeshStandardMaterial)) continue;
      let name: string;
      if (material.name !== '') {
        name = material.name;
      } else {
        do {
          lateMint += 1;
          name = `material_x${lateMint}`;
        } while (takenNames.has(name));
        takenNames.add(name);
        material.name = name;
      }
      const shipped: ShippedFactors = {
        metalnessMap: material.metalnessMap,
        roughnessMap: material.roughnessMap,
        metallic: material.metalness,
        roughness: material.roughness,
        normalScale: Math.abs(material.normalScale.x),
        occlusionStrength: material.aoMapIntensity,
        baseColor: [material.color.r, material.color.g, material.color.b],
        emissive: [material.emissive.r, material.emissive.g, material.emissive.b],
      };
      const measured = medianRoughness(material.roughnessMap);
      const entry = entries.get(name);
      if (entry !== undefined) {
        if (!entry.bearers.some((b) => b.material === material)) entry.bearers.push({ material, shipped });
        // NAMESAKES SHARE ONE ROW and one factor, so the row's ceiling must
        // serve the GLOSSIEST of them — keeping only the first bearer's
        // measurement leaves a satin material dictating a ceiling that cannot
        // drag its mirror-finish namesake to matte (Codex P2, #115).
        const first = entry.bearers[0]!.shipped;
        entry.target = {
          ...entry.target,
          bearerCount: entry.bearers.length,
          bearersDisagreeMetallic: entry.target.bearersDisagreeMetallic || first.metallic !== shipped.metallic,
          bearersDisagreeRoughness: entry.target.bearersDisagreeRoughness || first.roughness !== shipped.roughness,
          ...(measured !== null && (entry.target.measuredRoughness === null || measured < entry.target.measuredRoughness)
            ? { measuredRoughness: measured, roughnessCeiling: roughnessCeilingFor(measured) }
            : {}),
        };
        continue;
      }
      entries.set(name, {
        target: {
          name,
          shippedMetallic: shipped.metallic,
          shippedRoughness: shipped.roughness,
          shippedNormalScale: shipped.normalScale,
          shippedOcclusionStrength: shipped.occlusionStrength,
          shippedBaseColor: shipped.baseColor,
          shippedEmissive: shipped.emissive,
          hasRoughnessTexture: material.roughnessMap !== null,
          hasMetallicTexture: material.metalnessMap !== null,
          bearerCount: 1,
          bearersDisagreeMetallic: false,
          bearersDisagreeRoughness: false,
          measuredRoughness: measured,
          roughnessCeiling:
            material.roughnessMap === null ? 1 : measured === null ? LEGACY_ROUGHNESS_CEILING : roughnessCeilingFor(measured),
          hasNormalTexture: material.normalMap !== null,
          hasOcclusionTexture: material.aoMap !== null,
        },
        bearers: [{ material, shipped }],
      });
    }
  });
  scanned.set(scene, entries);
  return entries;
}

/** The subject's standard materials with their SHIPPED factors (first-touch snapshot). */
export function materialFactorTargets(scene: Object3D): MaterialFactorTarget[] {
  return [...scan(scene).values()].map((e) => e.target);
}

export interface MaterialFactorEditValues {
  readonly metallic?: number;
  readonly roughness?: number;
  readonly normalScale?: number;
  readonly occlusionStrength?: number;
  readonly baseColor?: Rgb;
  readonly emissive?: Rgb;
  /** false = ignore the metallicRoughness map on this channel (factor alone). */
  readonly metallicFromMap?: boolean;
  readonly roughnessFromMap?: boolean;
}

/**
 * Drive every standard material to `edits[name] ?? shipped` — the whole
 * truth every call, so an undone edit falls back to the file's own value
 * without anyone tracking deltas. Returns the edit names that matched no
 * material — the caller's loud-note evidence (a loaded Document can carry
 * factors onto a character that lacks the material; ADR-017 §7 posture).
 */
export function applyMaterialFactors(
  scene: Object3D,
  edits: ReadonlyMap<string, MaterialFactorEditValues>,
): { missing: readonly string[] } {
  const entries = scan(scene);
  for (const [name, entry] of entries) {
    const edit = edits.get(name);
    for (const { material, shipped } of entry.bearers) {
      // The fallback is THIS material's own snapshot — a namesake's file
      // values must never leak onto it (Codex P2, #87).
      material.metalness = edit?.metallic ?? shipped.metallic;
      material.roughness = edit?.roughness ?? shipped.roughness;
      // DETACH: glTF keeps roughness in G and metallic in B of one texture, and
      // three reads them through two independent slots — so one channel can
      // stop listening to the map while the other keeps listening. Absent means
      // attached (the file's own behaviour), never a stored "true".
      //
      // This is the cure the factor cannot give. Multiplying a roughness map of
      // ~0.04 up to matte needs ~x23, which amplifies the map's variation 23x
      // before clamping — blotchy — and saturates invisibly, since three clamps
      // effective roughness at 1.0.
      const nextMetalnessMap = edit?.metallicFromMap === false ? null : shipped.metalnessMap;
      const nextRoughnessMap = edit?.roughnessFromMap === false ? null : shipped.roughnessMap;
      // three recompiles the program when a map slot changes between null and a
      // texture, so only assign on an actual change.
      if (material.metalnessMap !== nextMetalnessMap) {
        material.metalnessMap = nextMetalnessMap;
        material.needsUpdate = true;
      }
      if (material.roughnessMap !== nextRoughnessMap) {
        material.roughnessMap = nextRoughnessMap;
        material.needsUpdate = true;
      }
      const normalMagnitude = edit?.normalScale ?? shipped.normalScale;
      const occlusion = edit?.occlusionStrength ?? shipped.occlusionStrength;
      const baseColor = edit?.baseColor ?? shipped.baseColor;
      const emissive = edit?.emissive ?? shipped.emissive;
      // Magnitude only — each component keeps its own sign (y-flip convention).
      const signX = material.normalScale.x < 0 ? -1 : 1;
      const signY = material.normalScale.y < 0 ? -1 : 1;
      material.normalScale.set(signX * normalMagnitude, signY * normalMagnitude);
      material.aoMapIntensity = occlusion;
      // setRGB in three's working space (linear sRGB) — the snapshot's space.
      material.color.setRGB(baseColor[0], baseColor[1], baseColor[2]);
      material.emissive.setRGB(emissive[0], emissive[1], emissive[2]);
    }
  }
  return { missing: [...edits.keys()].filter((name) => !entries.has(name)) };
}
